<template>
  <v-container fluid class="pa-0 pa-md-5">
    <v-row class="mb-5">
      <v-col cols="12">
        <v-card dark class="pa-4 rounded-lg d-flex align-center justify-space-between" outlined elevation="2" style="border-left: 5px solid #9c27b0">
          <div>
            <div class="text-overline mb-1 grey--text text--darken-1">Akzeptierte MTT Verträge</div>
            <div class="d-flex align-center">
              <div class="text-h4 primary--text font-weight-bold mr-2">{{ totalContractsCount }}</div>
              <v-chip x-small color="primary" outlined v-if="loading">lade...</v-chip>
            </div>
          </div>
          <v-icon style="opacity: 0.1; font-size: 60px">mdi-medical-bag</v-icon>
        </v-card>
      </v-col>
    </v-row>

    <v-card dark elevation="3" class="mb-6 rounded-lg">
      <v-card-text class="pa-4">
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="4">
            <v-select
              :items="studios"
              label="Studios filtern"
              v-model="studioSelect"
              item-text="name"
              item-value="_id"
              multiple
              outlined
              dense
              hide-details
              small-chips
              deletable-chips>
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text text-caption font-weight-bold"> {{ studioSelect.length }} Studios </span>
              </template>
            </v-select>
          </v-col>
          <v-col v-if="false" cols="12" sm="6" md="4">
            <v-text-field
              v-model="searchQuery"
              label="Suchen (Name, E-Mail)"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              @input="debounceSearch"
              placeholder="Eingeben..."></v-text-field>
          </v-col>
          <v-col cols="12" md="auto" class="d-flex justify-end align-center ml-auto">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="grey darken-1" @click="selectAllStudios" v-bind="attrs" v-on="on" class="mr-2">
                  <v-icon>{{
                    studioSelect.length === studios.length ? "mdi-checkbox-multiple-outline" : "mdi-checkbox-multiple-blank-outline"
                  }}</v-icon>
                </v-btn>
              </template>
              <span>Alle Studios an/abwählen</span>
            </v-tooltip>

            <v-btn icon color="primary" @click="fetchData(true)" :loading="loading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card dark elevation="0" class="mx-auto transparent" color="transparent">
      <v-row v-if="loading && page === 1" class="justify-center py-10">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </v-row>

      <v-row v-else-if="mttContracts.length === 0" class="justify-center py-10">
        <v-col cols="12" class="text-center grey--text text--lighten-1">
          <v-icon size="80" color="grey darken-2">mdi-inbox-outline</v-icon>
          <div class="text-h6 mt-4">Keine MTT Verträge gefunden</div>
          <div class="subtitle-2 grey--text text--darken-1">Versuche die Filtereinstellungen anzupassen</div>
        </v-col>
      </v-row>

      <div v-else>
        <v-expansion-panels v-model="userContractPanel" dark>
          <v-expansion-panel v-for="(user, i) of mttContracts" :key="user._id" class="mb-3 rounded-lg elevation-4">
            <v-expansion-panel-header :color="'#424242'">
              <template v-slot:default>
                <v-row no-gutters align="center">
                  <v-col cols="12" sm="4" class="d-flex align-center">
                    <span class="text-h6 font-weight-bold mr-2">{{ user.vorname }} {{ user.nachname }}</span>
                  </v-col>
                  <v-col cols="6" sm="3" class="text-caption grey--text text--lighten-2 d-flex align-center">
                    <v-icon x-small left color="grey lighten-2">mdi-map-marker</v-icon>
                    <span class="text-truncate">{{ getStudiosOfContracts(user.contracts) }}</span>
                  </v-col>
                  <v-col cols="6" sm="3" class="text-caption white--text d-flex align-center justify-end justify-sm-center">
                    <v-chip x-small label outlined class="mr-1" color="grey"> Start: {{ $moment(user.newestStartDate).format("DD.MM.YY") }} </v-chip>
                  </v-col>
                  <v-col cols="12" sm="2" class="text-right text-caption grey--text">
                    Erstellt: {{ $moment(user.newestCreatedAt).format("DD.MM HH:mm") }}
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="grey darken-4 pt-4">
              <v-expansion-panels v-model="openContractPanel" accordion flat>
                <v-expansion-panel v-for="(contract, j) of user.contracts" :key="contract._id" class="transparent">
                  <v-expansion-panel-header class="pl-0 py-2">
                    <div class="d-flex align-center" style="width: 100%">
                      <v-icon small class="mr-3" color="primary">mdi-file-document-outline</v-icon>
                      <div class="d-flex flex-column mr-auto">
                        <span class="subtitle-1 font-weight-medium">{{ getNameOfContract(contract.contractId) }}</span>
                      </div>
                      <div class="d-flex align-center">
                        <span class="text-caption grey--text">Start: {{ $moment(contract.startDate).format("DD.MM.YYYY") }}</span>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-card outlined color="grey darken-3" class="pa-3 fill-height">
                          <div class="subtitle-2 grey--text mb-2">Details & Verlauf</div>
                          <div v-if="contract.customer.contractCreatorInfos" class="mb-3 caption">
                            <v-icon x-small left>mdi-account-edit</v-icon>
                            Erstellt von: {{ contract.customer.contractCreatorInfos.firstName }} {{ contract.customer.contractCreatorInfos.lastName }}
                          </div>

                          <div v-if="contract.comments && contract.comments.length > 0" class="overflow-y-auto" style="max-height: 150px">
                            <div class="caption grey--text mb-1">Historie:</div>
                            <div v-for="(comment, cIdx) of contract.comments" :key="cIdx" class="caption mb-1">
                              <span class="grey--text">{{ $moment(comment.date).format("DD.MM HH:mm") }}:</span>
                              <span v-if="comment.status" class="font-weight-bold">{{ comment.status }}</span>
                              <span v-if="comment.comment"> - {{ comment.comment }}</span>
                            </div>
                          </div>
                        </v-card>
                      </v-col>

                      <v-col cols="12" md="6" class="d-flex flex-column justify-center align-center">
                        <v-btn
                          large
                          color="primary"
                          class="mb-3"
                          @click="printUserContract(user._id, contract.index, getNameOfContract(contract.contractId))">
                          <v-icon left>mdi-download</v-icon> PDF Herunterladen
                        </v-btn>
                        <v-btn outlined color="grey" @click="$router.push('/dashboard/user/search/' + user._id)">
                          <v-icon left>mdi-account</v-icon> Zum Kundenprofil
                        </v-btn>
                      </v-col>
                    </v-row>

                    <v-expand-transition>
                      <div v-if="openContractPanel === j" class="mt-4">
                        <object
                          v-if="!isApp && contractSauce"
                          width="100%"
                          height="400px"
                          :data="contractSauce"
                          class="rounded border-white"></object>
                      </div>
                    </v-expand-transition>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="text-center mt-6" v-if="page < totalPages">
          <v-btn :loading="loadingMore" @click="loadMore" color="primary" rounded outlined large>
            <v-icon left>mdi-chevron-down</v-icon> Mehr laden ({{ mttContracts.length }} / {{ totalContractsCount }})
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import api from "../../../api"

export default {
  data() {
    return {
      mttContracts: [],
      studios: [],
      studioSelect: [],
      searchQuery: "",

      page: 1,
      limit: 20,
      totalPages: 1,
      totalContractsCount: 0,

      loading: true,
      loadingMore: false,
      debounceTimeout: null,

      userContractPanel: null,
      openContractPanel: null,
      contractSauce: null,

      contractsTemplates: [], // Um Namen der Verträge aufzulösen
    }
  },

  async created() {
    try {
      const [studios, userStudio] = await Promise.all([api.fetchStudios(), api.getUserStudio()])
      this.studios = studios
      if (userStudio && userStudio.studio) {
        this.studioSelect = [userStudio.studio._id]
      }
      this.contractsTemplates = await api.fetchContracts()
      this.fetchData(true)
    } catch (e) {
      console.error("Init Error", e)
    }
  },

  computed: {
    isApp() {
      return window.flutter_inappwebview || false
    },
  },

  methods: {
    selectAllStudios() {
      if (this.studioSelect.length === this.studios.length) {
        // Wenn schon alle ausgewählt sind, wähle alle ab
        this.studioSelect = []
      } else {
        // Ansonsten wähle alle aus
        this.studioSelect = this.studios.map((s) => s._id)
      }
    },
    async fetchData(reset = false) {
      if (reset) {
        this.page = 1
        this.loading = true
        this.mttContracts = []
      } else {
        this.loadingMore = true
      }

      try {
        const params = {
          page: this.page,
          limit: this.limit,
          studios: this.studioSelect,
          search: this.searchQuery,
        }

        const response = await api.fetchMTTAcceptedContracts(params)

        let newUsers = []
        if (response && response.users) {
          newUsers = response.users
          this.totalContractsCount = response.total
          this.totalPages = response.pages
        } else if (Array.isArray(response)) {
          newUsers = response
          this.totalContractsCount = newUsers.length
          this.totalPages = 1
        }

        if (reset) {
          this.mttContracts = newUsers
        } else {
          this.mttContracts = [...this.mttContracts, ...newUsers]
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Verträge")
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    loadMore() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchData(false)
      }
    },

    debounceSearch() {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.fetchData(true)
      }, 500)
    },

    getNameOfStudio(studioId) {
      let studio = this.studios.find((e) => e._id === studioId)
      return studio && studio.name ? studio.name : "Unbekannt"
    },

    getStudiosOfContracts(contracts) {
      if (!contracts) return ""
      let studios = contracts.map((e) => e.studio._id)
      studios = studios.map((e) => this.getNameOfStudio(e))
      return [...new Set(studios)].join(", ")
    },

    getNameOfContract(contractId) {
      let contract = this.contractsTemplates.find((e) => e._id === contractId)
      return contract?.internalTitle || "Vertrag"
    },

    printUserContract(userId, index, title) {
      const safeTitle = (title || "Vertrag").replace(/[\\/:"*?<>|]/g, "_")
      api.printUserContract(userId, index, safeTitle)
    },

    async loadPdfPreview(userId, contractIndex) {
      this.contractSauce = null
      try {
        let blob = await api.getIframeprintUserContract(userId, contractIndex, "Vorschau")
        this.contractSauce = URL.createObjectURL(blob)
      } catch (e) {
        console.error("PDF Load Error", e)
        this.$toast.error("Vorschau konnte nicht geladen werden")
      }
    },
  },

  watch: {
    studioSelect(val) {
      if (val.length > 0) {
        this.fetchData(true)
      }
    },
    async openContractPanel(val) {
      if (val !== undefined && this.userContractPanel !== undefined) {
        let user = this.mttContracts[this.userContractPanel]
        if (user && user.contracts && user.contracts[val]) {
          let contract = user.contracts[val]
          await this.loadPdfPreview(user._id, contract.index)
        }
      } else {
        this.contractSauce = null
      }
    },
    userContractPanel() {
      this.openContractPanel = undefined
      this.contractSauce = null
    },
  },
}
</script>

<style scoped>
.transparent {
  background-color: transparent !important;
}
.border-white {
  border: 1px solid rgba(255, 255, 255, 0.5);
}
</style>
